import { useEffect, useState } from 'react';
import { adminApi } from '@/lib/api';
import { 
  Search, 
  Edit, 
  Trash2,
  Plus,
  Loader2,
  MessageSquare,
  Phone,
  Mail,
  MapPin
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
} from '@/components/ui/pagination';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const formatDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDateOnly = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const getStatusColor = (status) => {
  const colors = {
    pending: 'bg-yellow-100 text-yellow-800',
    new: 'bg-[#C97A5E]/10 text-[#C97A5E]',
    contacted: 'bg-blue-100 text-blue-800',
    qualified: 'bg-purple-100 text-purple-800',
    trial_pack_shared: 'bg-orange-100 text-orange-800',
    converted: 'bg-green-100 text-green-800',
    closed: 'bg-gray-100 text-gray-800'
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

const LEAD_SOURCE_OPTIONS = [
  'Whatsapp',
  'Instagram',
  'Youtube',
  'Google Ad',
  'Facebook Ad',
  'In person',
  'Reference',
  'LinkedIn'
];

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [formData, setFormData] = useState({ status: '', notes: '' });
  const [createForm, setCreateForm] = useState({
    name: '',
    phone: '',
    email: '',
    location: '',
    inquiry_type: '',
    message: '',
    lead_source: 'In person',
    reference_details: '',
    sales_staff_id: ''
  });
  const [staffOptions, setStaffOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [leadDetailLoading, setLeadDetailLoading] = useState(false);
  const [actionForm, setActionForm] = useState({ message: '', status: '', follow_up_date: '' });
  const [actionSaving, setActionSaving] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  useEffect(() => {
    fetchLeads();
    fetchSalesStaff();
  }, [search, statusFilter, page]);

  const fetchLeads = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status = statusFilter;
      
      params.page = page;
      params.page_size = pageSize;

      const response = await adminApi.getLeads(params);
      setLeads(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  };

  const fetchSalesStaff = async () => {
    try {
      const response = await adminApi.getSalesStaff();
      setStaffOptions(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch sales staff');
    }
  };

  const loadLeadDetail = async (leadId) => {
    setLeadDetailLoading(true);
    try {
      const response = await adminApi.getLeadById(leadId);
      setSelectedLead(response.data);
    } catch (error) {
      toast.error('Failed to load lead details');
    } finally {
      setLeadDetailLoading(false);
    }
  };

  const handleEdit = (lead) => {
    setSelectedLead(lead);
    setFormData({ status: lead.status, notes: lead.notes });
    setActionForm({ message: '', status: lead.status || '', follow_up_date: '' });
    setShowEditModal(true);
    loadLeadDetail(lead.id);
  };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      await adminApi.updateLead(selectedLead.id, formData);
      toast.success('Lead updated successfully');
      setShowEditModal(false);
      fetchLeads();
    } catch (error) {
      toast.error('Failed to update lead');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await adminApi.deleteLead(selectedLead.id);
      toast.success('Lead deleted successfully');
      setShowDeleteDialog(false);
      setSelectedLead(null);
      fetchLeads();
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  const handleAddAction = async () => {
    if (!actionForm.message.trim()) {
      toast.error('Please enter an update message');
      return;
    }

    setActionSaving(true);
    try {
      await adminApi.addLeadAction(selectedLead.id, {
        message: actionForm.message.trim(),
        status: actionForm.status || undefined,
        follow_up_date: actionForm.follow_up_date || undefined
      });
      toast.success('Lead update added');
      setActionForm({ message: '', status: actionForm.status || '', follow_up_date: '' });
      await loadLeadDetail(selectedLead.id);
      fetchLeads();
    } catch (error) {
      toast.error('Failed to add lead update');
    } finally {
      setActionSaving(false);
    }
  };

  const handleCreateLead = async () => {
    if (!createForm.name.trim() || !createForm.phone.trim() || !createForm.sales_staff_id) {
      toast.error('Name, phone and sales staff are required');
      return;
    }
    setSaving(true);
    try {
      await adminApi.createLeadManual({
        name: createForm.name.trim(),
        phone: createForm.phone.trim(),
        email: createForm.email.trim(),
        location: createForm.location.trim(),
        inquiry_type: createForm.inquiry_type.trim(),
        message: createForm.message.trim(),
        lead_source: createForm.lead_source,
        reference_details: createForm.reference_details.trim(),
        sales_staff_id: createForm.sales_staff_id
      });
      toast.success('Lead created successfully');
      setShowCreateModal(false);
      setCreateForm({ name: '', phone: '', email: '', location: '', inquiry_type: '', message: '', lead_source: 'In person', reference_details: '', sales_staff_id: '' });
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create lead');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="leads-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#2D2420]">Leads</h1>
          <p className="text-[#A8A8A8] mt-1">Manage all leads and inquiries</p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-[#C97A5E] hover:bg-[#B06850]"
          data-testid="add-lead-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Lead
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A8A8A8]" />
              <Input
                placeholder="Search by name, phone, or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 border-[#E5E5E5] focus:border-[#C97A5E]"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48 border-[#E5E5E5]" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="qualified">Qualified</SelectItem>
                <SelectItem value="trial_pack_shared">Trial pack shared</SelectItem>
                <SelectItem value="converted">Converted</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Leads Table */}
      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
            </div>
          ) : leads.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-[#A8A8A8]">
              <MessageSquare className="w-12 h-12 mb-4" />
              <p>No leads found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Ref</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Name</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Contact</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Location</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Inquiry</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Message</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Sales Staff</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Status</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Date</th>
                    <th className="text-right py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]">
                  {leads.map((l) => (
                    <tr key={l.id} className="hover:bg-[#FAFAF9]/50 transition-colors" data-testid={`lead-row-${l.id}`}>
                      <td className="py-4 px-6 text-[#A8A8A8]">{l.lead_ref_no || '-'}</td>
                      <td className="py-4 px-6 font-medium text-[#2D2420]">{l.name}</td>
                      <td className="py-4 px-6">
                        <div className="space-y-1">
                          <div className="flex items-center gap-1 text-sm text-[#5C4033]">
                            <Phone className="w-3 h-3" /> {l.phone}
                          </div>
                          {l.email && (
                            <div className="flex items-center gap-1 text-sm text-[#A8A8A8]">
                              <Mail className="w-3 h-3" /> {l.email}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-6 text-[#A8A8A8]">{l.location || '-'}</td>
                      <td className="py-4 px-6">
                        <Badge className="bg-[#D2B48C]/20 text-[#5C4033]">
                          {l.inquiry_type || 'General'}
                        </Badge>
                      </td>
                      <td className="py-4 px-6 max-w-xs">
                        <p className="text-sm text-[#A8A8A8] truncate">{l.message || '-'}</p>
                      </td>
                      <td className="py-4 px-6 text-sm text-[#2D2420]">
                        {l.sales_staff_name || '-'}
                      </td>
                      <td className="py-4 px-6">
                        <Badge className={cn("text-xs", getStatusColor(l.status))}>
                          {l.status}
                        </Badge>
                      </td>
                      <td className="py-4 px-6 text-sm text-[#A8A8A8]">{formatDate(l.created_at)}</td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(l)}
                            className="text-[#5C4033] hover:bg-[#FAFAF9]"
                            data-testid={`edit-lead-${l.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => { setSelectedLead(l); setShowDeleteDialog(true); }}
                            className="text-red-600 hover:bg-red-50"
                            data-testid={`delete-lead-${l.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
        {!loading && total > 0 && (
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-6 py-4 border-t border-[#E5E5E5]">
            <p className="text-sm text-[#A8A8A8]">
              Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, total)} of {total}
            </p>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={(e) => {
                      e.preventDefault();
                      setPage((prev) => Math.max(1, prev - 1));
                    }}
                    className={page === 1 ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>
                <PaginationItem>
                  <span className="px-3 text-sm text-[#5C4033]">
                    Page {page} of {Math.max(1, Math.ceil(total / pageSize))}
                  </span>
                </PaginationItem>
                <PaginationItem>
                  <PaginationNext
                    onClick={(e) => {
                      e.preventDefault();
                      setPage((prev) => Math.min(Math.max(1, Math.ceil(total / pageSize)), prev + 1));
                    }}
                    className={page >= Math.max(1, Math.ceil(total / pageSize)) ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </Card>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Update Lead</DialogTitle>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-4 py-4">
              {/* Lead Info */}
              <div className="p-4 bg-[#FAFAF9] rounded-xl space-y-2">
                <p className="font-medium text-[#2D2420]">{selectedLead.name}</p>
                <div className="flex items-center gap-2 text-sm text-[#5C4033]">
                  <Phone className="w-4 h-4" /> {selectedLead.phone}
                </div>
                {selectedLead.lead_ref_no && (
                  <div className="text-sm text-[#A8A8A8]">Ref: {selectedLead.lead_ref_no}</div>
                )}
                {selectedLead.email && (
                  <div className="flex items-center gap-2 text-sm text-[#A8A8A8]">
                    <Mail className="w-4 h-4" /> {selectedLead.email}
                  </div>
                )}
                {selectedLead.location && (
                  <div className="flex items-center gap-2 text-sm text-[#A8A8A8]">
                    <MapPin className="w-4 h-4" /> {selectedLead.location}
                  </div>
                )}
              </div>

              {selectedLead.message && (
                <div className="p-4 bg-[#FAFAF9] rounded-xl">
                  <p className="text-xs text-[#A8A8A8] mb-1">Message</p>
                  <p className="text-sm text-[#2D2420]">{selectedLead.message}</p>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-[#5C4033]">Status</Label>
                <Select 
                  value={formData.status} 
                  onValueChange={(v) => setFormData({ ...formData, status: v })}
                >
                  <SelectTrigger className="border-[#E5E5E5]" data-testid="lead-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="qualified">Qualified</SelectItem>
                    <SelectItem value="trial_pack_shared">Trial pack shared</SelectItem>
                    <SelectItem value="converted">Converted</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-[#5C4033]">Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Add notes about this lead..."
                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
                  data-testid="lead-notes-input"
                />
              </div>

              <div className="border-t border-[#E5E5E5] pt-4 space-y-4">
                <div>
                  <p className="text-sm font-semibold text-[#2D2420]">Add Lead Update</p>
                  <p className="text-xs text-[#A8A8A8]">Record a follow-up or status change</p>
                </div>

                <div className="space-y-2">
                  <Label className="text-[#5C4033]">Message</Label>
                  <Textarea
                    value={actionForm.message}
                    onChange={(e) => setActionForm({ ...actionForm, message: e.target.value })}
                    placeholder="Call summary, next step, etc."
                    className="border-[#E5E5E5] focus:border-[#C97A5E]"
                    data-testid="lead-action-message"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-[#5C4033]">Status</Label>
                    <Select
                      value={actionForm.status}
                      onValueChange={(v) => setActionForm({ ...actionForm, status: v })}
                    >
                      <SelectTrigger className="border-[#E5E5E5]">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="contacted">Contacted</SelectItem>
                        <SelectItem value="qualified">Qualified</SelectItem>
                        <SelectItem value="trial_pack_shared">Trial pack shared</SelectItem>
                        <SelectItem value="converted">Converted</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#5C4033]">Follow-up Date</Label>
                    <Input
                      type="date"
                      value={actionForm.follow_up_date}
                      onChange={(e) => setActionForm({ ...actionForm, follow_up_date: e.target.value })}
                      className="border-[#E5E5E5] focus:border-[#C97A5E]"
                      data-testid="lead-action-followup"
                    />
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={handleAddAction}
                    disabled={actionSaving}
                    className="bg-[#5C4033] hover:bg-[#4A3228]"
                    data-testid="lead-action-save"
                  >
                    {actionSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Add Update
                  </Button>
                </div>
              </div>

              <div className="border-t border-[#E5E5E5] pt-4 space-y-3">
                <p className="text-sm font-semibold text-[#2D2420]">Update History</p>
                {leadDetailLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-5 h-5 animate-spin text-[#C97A5E]" />
                  </div>
                ) : (selectedLead.actions || []).length === 0 ? (
                  <p className="text-sm text-[#A8A8A8]">No updates added yet.</p>
                ) : (
                  <div className="space-y-3 max-h-56 overflow-y-auto">
                    {(selectedLead.actions || []).slice().reverse().map((action) => (
                      <div key={action.id} className="p-3 rounded-lg border border-[#E5E5E5] bg-white">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-[#2D2420]">{action.message}</p>
                          {action.status && (
                            <Badge className={cn("text-xs", getStatusColor(action.status))}>
                              {action.status}
                            </Badge>
                          )}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-3 text-xs text-[#A8A8A8]">
                          <span>Created: {formatDate(action.created_at)}</span>
                          {action.created_by_name && (
                            <span>By: {action.created_by_name}</span>
                          )}
                          {action.follow_up_date && (
                            <span>Follow-up: {formatDateOnly(action.follow_up_date)}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEditModal(false)}
              className="border-[#E5E5E5]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              disabled={saving}
              className="bg-[#C97A5E] hover:bg-[#B06850]"
              data-testid="save-lead-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Lead Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Create Lead</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Name *</Label>
              <Input
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                placeholder="Enter name"
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
                data-testid="create-lead-name"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Phone *</Label>
              <Input
                value={createForm.phone}
                onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                placeholder="Enter phone"
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
                data-testid="create-lead-phone"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Sales Staff *</Label>
              <Select value={createForm.sales_staff_id} onValueChange={(v) => setCreateForm({ ...createForm, sales_staff_id: v })}>
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select sales staff" />
                </SelectTrigger>
                <SelectContent>
                  {staffOptions.map((staff) => (
                    <SelectItem key={staff.id} value={staff.id}>{staff.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Email</Label>
              <Input
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                placeholder="Enter email"
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
                data-testid="create-lead-email"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Location</Label>
              <Input
                value={createForm.location}
                onChange={(e) => setCreateForm({ ...createForm, location: e.target.value })}
                placeholder="Enter location"
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
                data-testid="create-lead-location"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Lead Source</Label>
              <Select value={createForm.lead_source} onValueChange={(v) => setCreateForm({ ...createForm, lead_source: v })}>
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select lead source" />
                </SelectTrigger>
                <SelectContent>
                  {LEAD_SOURCE_OPTIONS.map((source) => (
                    <SelectItem key={source} value={source}>{source}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Reference Details</Label>
              <Input
                value={createForm.reference_details}
                onChange={(e) => setCreateForm({ ...createForm, reference_details: e.target.value })}
                placeholder="Enter reference details"
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
                data-testid="create-lead-reference-details"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Inquiry Type</Label>
              <Input
                value={createForm.inquiry_type}
                onChange={(e) => setCreateForm({ ...createForm, inquiry_type: e.target.value })}
                placeholder="Enter inquiry type"
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
                data-testid="create-lead-inquiry"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Message</Label>
              <Textarea
                value={createForm.message}
                onChange={(e) => setCreateForm({ ...createForm, message: e.target.value })}
                placeholder="Enter message"
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
                data-testid="create-lead-message"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateModal(false)}
              className="border-[#E5E5E5]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateLead}
              disabled={saving}
              className="bg-[#C97A5E] hover:bg-[#B06850]"
              data-testid="create-lead-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Lead</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this lead from "{selectedLead?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-lead-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
